import React from 'react';
import { GeoJsonLayer, TextLayer } from '@deck.gl/layers';
import { DeckGL } from '@deck.gl/react';
import { type Layer } from '@deck.gl/core';
import { useUSStatesGeo } from './useUSStatesGeo';
import { useWebglContextRecovery } from './useWebglContextRecovery';
import { transformCoord, transformFeature, NAME_TO_ABBR, STATE_LABEL_RAW } from './usMapProjection';
import { computeStatesMapViewState, visibleBounds } from './mapFitViewState';

interface InteractiveStatesMapProps {
  selectedStates: string[];
  onChange: (next: string[]) => void;
  accentColor: string;
  // 2-letter codes the user may toggle. Disallowed states render dimmed
  // and clicks/hover are no-ops on them. When null/undefined, all states
  // are allowed (legacy stations with no declared coveredStates).
  allowedStates?: string[] | null;
  // 2-letter codes marking the publisher's coverage. When set, the view
  // fits the selectable region (allowedStates when provided, else these);
  // when unset the map opens on the national view (zoom 2.8).
  focusStates?: string[];
}

const MAP_HEIGHT = 420;

const InteractiveStatesMap: React.FC<InteractiveStatesMapProps> = ({
  selectedStates,
  onChange,
  accentColor,
  allowedStates,
  focusStates,
}) => {
  const { data: rawGeoJson } = useUSStatesGeo();
  const { deckKey, onDeviceInitialized, exhausted, reset } = useWebglContextRecovery();
  const [hoveredState, setHoveredState] = React.useState<string | null>(null);

  // Apply the AK/HI/PR repositioning to the raw GeoJSON only once per
  // payload. Cached via useMemo so re-renders don't re-walk every polygon.
  const statesGeoJson = React.useMemo<GeoJSON.FeatureCollection | null>(() => {
    if (!rawGeoJson) return null;
    return { ...rawGeoJson, features: rawGeoJson.features.map(transformFeature) };
  }, [rawGeoJson]);

  const selectedSet = React.useMemo(() => new Set(selectedStates), [selectedStates]);

  // null/undefined here ≡ "no restriction" (full national map, every state
  // selectable). When the caller passes a list we honor it strictly.
  const allowedSet = React.useMemo<Set<string> | null>(() => {
    if (!allowedStates) return null;
    return new Set(allowedStates);
  }, [allowedStates]);

  const toggleState = React.useCallback(
    (abbr: string) => {
      if (allowedSet && !allowedSet.has(abbr)) return;
      if (selectedSet.has(abbr)) {
        onChange(selectedStates.filter((s) => s !== abbr));
      } else {
        onChange([...selectedStates, abbr]);
      }
    },
    [allowedSet, selectedSet, selectedStates, onChange],
  );

  // Track the map's actual rendered width so the fit is correct at every
  // screen size (deck.gl doesn't auto-refit to its container). Measured
  // before paint via useLayoutEffect, then kept current with a
  // ResizeObserver. 0 until measured; the view-state memo falls back to a
  // nominal width for that first pass.
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const mapWidth = containerWidth || 800;

  // The static view the map shows (controller interactions are disabled,
  // so this IS the map). Fits the selectable region (allowedStates when
  // declared, else the coverage itself) at the measured container size,
  // refit on resize.
  const viewState = React.useMemo(() => {
    const hasFocus = !!focusStates && focusStates.length > 0;
    const fitAbbrs = hasFocus
      ? (allowedStates && allowedStates.length > 0 ? allowedStates : focusStates!)
      : null;
    return computeStatesMapViewState(statesGeoJson, fitAbbrs, mapWidth, MAP_HEIGHT);
  }, [statesGeoJson, focusStates, allowedStates, mapWidth]);

  // For each state, two things: the viewport bbox the map will show, and
  // a fallback "visible bbox center" we can use when a label's hand-tuned
  // default falls outside that viewport. Bounding-box center (not vertex
  // centroid) avoids the coastline-vertex bias.
  // Empty when focusStates is unset; national-view labels stay at the
  // static hand-tuned positions in STATE_LABEL_RAW.
  const labelRelocation = React.useMemo<{
    viewportBbox: [number, number, number, number] | null;
    centroidsByAbbr: Record<string, [number, number]>;
  }>(() => {
    if (!statesGeoJson || !focusStates || focusStates.length === 0) {
      return { viewportBbox: null, centroidsByAbbr: {} };
    }
    // getBounds returns a flat [west, south, east, north] 4-tuple.
    const viewportBbox = visibleBounds(viewState, mapWidth, MAP_HEIGHT);
    if (!viewportBbox) {
      return { viewportBbox: null, centroidsByAbbr: {} };
    }

    const centroids: Record<string, [number, number]> = {};
    const expandStateBbox = (
      coords: unknown,
      acc: [number, number, number, number],
    ): void => {
      if (typeof (coords as number[])[0] === 'number') {
        const [lng, lat] = coords as [number, number];
        if (
          lng >= viewportBbox[0] && lng <= viewportBbox[2] &&
          lat >= viewportBbox[1] && lat <= viewportBbox[3]
        ) {
          if (lng < acc[0]) acc[0] = lng;
          if (lat < acc[1]) acc[1] = lat;
          if (lng > acc[2]) acc[2] = lng;
          if (lat > acc[3]) acc[3] = lat;
        }
        return;
      }
      for (const c of coords as unknown[]) expandStateBbox(c, acc);
    };
    for (const feat of statesGeoJson.features) {
      const name = (feat.properties as Record<string, string> | null)?.name ?? '';
      const abbr = NAME_TO_ABBR[name];
      if (!abbr || !feat.geometry) continue;
      const geom = feat.geometry;
      if (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon') continue;
      const stateBbox: [number, number, number, number] = [180, 90, -180, -90];
      expandStateBbox(geom.coordinates, stateBbox);
      if (stateBbox[0] <= stateBbox[2] && stateBbox[1] <= stateBbox[3]) {
        centroids[abbr] = [
          (stateBbox[0] + stateBbox[2]) / 2,
          (stateBbox[1] + stateBbox[3]) / 2,
        ];
      }
    }
    return { viewportBbox, centroidsByAbbr: centroids };
  }, [statesGeoJson, focusStates, viewState, mapWidth]);

  const labels = React.useMemo(
    () =>
      STATE_LABEL_RAW.flatMap(([text, lng, lat]) => {
        // Zoomed view: only show labels for allowed states (we don't want
        // to render text on dimmed, non-selectable states). Use the
        // hand-tuned default position if it's inside the viewport;
        // otherwise fall back to the visible-bbox center for that state
        // so a partially-visible state still gets a label in-frame.
        if (focusStates && focusStates.length > 0) {
          if (allowedSet && !allowedSet.has(text)) return [];
          const { viewportBbox, centroidsByAbbr } = labelRelocation;
          if (!viewportBbox) return [];
          const [defaultLng, defaultLat] = transformCoord(lng, lat);
          const defaultInside =
            defaultLng >= viewportBbox[0] && defaultLng <= viewportBbox[2] &&
            defaultLat >= viewportBbox[1] && defaultLat <= viewportBbox[3];
          if (defaultInside) {
            return [{ position: [defaultLng, defaultLat] as [number, number], text }];
          }
          const fallback = centroidsByAbbr[text];
          if (!fallback) return [];
          return [{ position: fallback, text }];
        }
        const [tLng, tLat] = transformCoord(lng, lat);
        return [{ position: [tLng, tLat] as [number, number], text }];
      }),
    [labelRelocation, focusStates, allowedSet],
  );

  // Parse the accent color (hex, "#RGB" or "#RRGGBB") into the RGBA tuple
  // deck.gl wants. Defaults silently to a neutral blue if the prop isn't a
  // recognizable hex — the picker still works without color customization.
  const accentRgb = React.useMemo<[number, number, number]>(() => {
    const hex = accentColor.replace('#', '');
    const expand = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    if (!/^[0-9a-f]{6}$/i.test(expand)) return [37, 99, 235];
    return [
      parseInt(expand.slice(0, 2), 16),
      parseInt(expand.slice(2, 4), 16),
      parseInt(expand.slice(4, 6), 16),
    ];
  }, [accentColor]);

  const layers = React.useMemo(() => {
    const result: Layer[] = [];

    if (statesGeoJson) {
      result.push(
        new GeoJsonLayer({
          id: 'states-interactive',
          data: statesGeoJson,
          filled: true,
          stroked: true,
          pickable: true,
          getFillColor: (f: GeoJSON.Feature) => {
            const name = (f.properties as Record<string, string>)?.name || '';
            const abbr = NAME_TO_ABBR[name] || '';
            if (selectedSet.has(abbr)) return [...accentRgb, 255];
            // Disallowed states render as a flat very-light gray with no
            // hover affordance — visually present but clearly inert.
            if (allowedSet && !allowedSet.has(abbr)) return [241, 245, 249, 255];
            if (hoveredState === abbr) return [...accentRgb, 110];
            return [224, 231, 239, 255];
          },
          getLineColor: [255, 255, 255, 255],
          getLineWidth: 1,
          lineWidthUnits: 'pixels' as const,
          onClick: (info: { object?: GeoJSON.Feature }) => {
            if (!info.object) return;
            const name = (info.object.properties as Record<string, string>)?.name || '';
            const abbr = NAME_TO_ABBR[name];
            if (abbr) toggleState(abbr);
          },
          onHover: (info: { object?: GeoJSON.Feature }) => {
            if (info.object) {
              const name = (info.object.properties as Record<string, string>)?.name || '';
              const abbr = NAME_TO_ABBR[name] || null;
              // Skip hover highlight on disallowed states so the user
              // doesn't get false-positive interactivity cues.
              if (allowedSet && abbr && !allowedSet.has(abbr)) {
                setHoveredState(null);
              } else {
                setHoveredState(abbr);
              }
            } else {
              setHoveredState(null);
            }
          },
          updateTriggers: {
            getFillColor: [selectedStates, hoveredState, accentRgb, allowedStates],
          },
        }),
      );
    }

    if (labels.length > 0) {
      result.push(
        new TextLayer({
          id: 'state-labels',
          data: labels,
          getPosition: (d: { position: number[] }) => d.position as [number, number],
          getText: (d: { text: string }) => d.text,
          getSize: 11,
          getColor: (d: { text: string }) =>
            selectedSet.has(d.text) ? [255, 255, 255, 255] : [100, 116, 139, 255],
          fontWeight: 600,
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          sizeUnits: 'pixels' as const,
          billboard: false,
          updateTriggers: {
            getColor: [selectedStates],
          },
        }),
      );
    }

    return result;
  }, [statesGeoJson, labels, selectedSet, hoveredState, selectedStates, toggleState, accentRgb, allowedSet, allowedStates]);

  return (
    <div
      ref={containerRef}
      className="rounded-lg border border-slate-200 overflow-hidden relative bg-white"
      style={{ height: MAP_HEIGHT }}
      // deck.gl's onHover doesn't always fire with no info when the cursor
      // exits the canvas across an edge (no intermediate pointer event in
      // empty whitespace), so the hover highlight stays "stuck" on the
      // last state. Clearing on the outer div's mouseleave covers every
      // exit direction.
      onMouseLeave={() => setHoveredState(null)}
    >
      {/* CTV-689: remount the Deck (fresh canvas/context) after a WebGL
          context loss, with a manual reload once the recovery cap is hit. */}
      {exhausted ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-50 text-[12px] text-slate-500">
          <span>Map rendering was interrupted by the browser.</span>
          <button
            type="button"
            onClick={reset}
            className="rounded-md border border-slate-300 bg-white px-3 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-100"
          >
            Reload map
          </button>
        </div>
      ) : (
        <DeckGL
          key={deckKey}
          onDeviceInitialized={onDeviceInitialized}
          viewState={viewState}
          controller={{
            scrollZoom: false,
            dragPan: false,
            dragRotate: false,
            doubleClickZoom: false,
            touchZoom: false,
            touchRotate: false,
            keyboard: false,
          }}
          layers={layers}
          style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', background: '#ffffff' }}
          getCursor={({ isHovering }: { isHovering: boolean }) => (isHovering ? 'pointer' : 'default')}
        />
      )}
    </div>
  );
};

export default InteractiveStatesMap;
